import { FC, useEffect } from 'react'
import * as moment from 'moment'
import * as styles from 'components/features/pfm/CarSettingsModule/CarSettingsModule.css'
import { Car } from 'interfaces/property'
import Table, { TableColumnDefinition, TableComponentType } from 'components/molecules/tables/Table'
import TextButton from 'components/atoms/buttons/TextButton'
import { BodyText } from 'components/atoms/Text'
import { DATE_FORMAT_US } from 'constants/datetime'
import { formatTableCurrency } from 'helpers/tableHelpers'
import { deleteCarActionCreator, getCarMakesActionCreator } from 'actions/propertyActions'
import { toggleCarEditActionCreator } from 'actions/settingsActions'
import AbsentValue from 'components/atoms/AbsentValue'
import { Editable, SortingProp } from 'interfaces/table'
import { CarEditHandler } from 'components/features/pfm/CarEditHandler/CarEditHandler'
import { CarDeleteHandler } from 'components/features/pfm/CarDeleteHandler/CarDeleteHandler'
import { CarUnlinkHandler } from 'components/features/pfm/CarUnlinkHandler/CarUnlinkHandler'
import { useStoreSelector, useStoreDispatch } from 'helpers/reactReduxHelpers'
import { carsArraySelector } from 'selectors/propertySelectors'
import { Guards } from 'interfaces/guards'
import { carsGuard } from 'guards/propertyGuards'
import { connectShadedLoadable } from 'helpers/loadableHelpers'

export type CarSettingsStateProps = {
  cars: Car[]
  editId: null | number
  unlinkingId: null | number
  deletingId: null | number
}

export type CarSettingsDispatchProps = {
  edit: typeof toggleCarEditActionCreator
  cancelEdit: () => void

  hideModal: () => void
  showUnlinkCarModal: (carId: number) => void
  unlink: (id: number) => void
  showRemoveCarModal: (carId: number) => void
  remove: typeof deleteCarActionCreator

  getCarMakes: typeof getCarMakesActionCreator
}

type CarSettingsProps = {}

const TypedTable = Table as TableComponentType<Editable<Car>>

const CarSettingsModuleComponent: FC<CarSettingsProps> = (props) => {
  const {} = props

  const cars = useStoreSelector(carsArraySelector)

  const dispatch = useStoreDispatch()
  useEffect(() => {
    dispatch(getCarMakesActionCreator())
  }, [dispatch])


  if (cars.length === 0) {
    return (
      <BodyText className={ styles.emptyState }>
        You have no linked cars.
      </BodyText>
    )
  }

  const MAKE_COLUMN: TableColumnDefinition<Car, string> = {
    name: 'make',
    title: 'Make',
    propsSelector: ({ make }) => make.name,
    sortingSelector: ({ make }) => make.name, // SAME_SORTING_SELECTOR does not work here
  }

  const MODEL_COLUMN: TableColumnDefinition<Car, string> = {
    name: 'model',
    title: 'Model',
    propsSelector: ({ model }) => model.name,
    sortingSelector: ({ model }) => model.name, // SAME_SORTING_SELECTOR does not work here
  }

  const YEAR_COLUMN: TableColumnDefinition<Car, number> = {
    name: 'year',
    title: 'Year',
    propsSelector: ({ year }) => year,
    sortingSelector: ({ year }) => year, // SAME_SORTING_SELECTOR does not work here
  }

  const VALUE_COLUMN: TableColumnDefinition<Car, number> = {
    name: 'value',
    title: (
      <span>
        Estimated <span className={ styles.hideForSmall }>Current</span> Value
      </span>
    ),
    component: formatTableCurrency,
    propsSelector: ({ estimated_value }) => estimated_value,
    sortingSelector: ({ estimated_value }) => estimated_value, // SAME_SORTING_SELECTOR does not work here
  }

  const EDIT_BUTTON: TableColumnDefinition<Car, Car> = {
    name: 'edit',
    title: '',
    component: (car) => {
      if (car.unlinked_at) {
        return (
          <AbsentValue children={ `Unlinked on ${ moment(car.unlinked_at).format(DATE_FORMAT_US) }` } />
        )
      }

      return (
        <CarEditHandler carId={ car.id }>
          <TextButton qaMarker='edit-button'>
            Edit
          </TextButton>
        </CarEditHandler>
      )
    },
    getColSpan: (car) => car.unlinked_at ? 2 : 1,
  }

  const UNLINK_BUTTON: TableColumnDefinition<Car, Car> = {
    name: 'unlink',
    title: '',
    component: (car) =>
      car.unlinked_at ?
        null :
        (
          <CarUnlinkHandler carId={ car.id }>
            <TextButton qaMarker='unlink-button'>
              Unlink
            </TextButton>
          </CarUnlinkHandler>
        ),
    getColSpan: (car) => car.unlinked_at ? 0 : 1,
  }

  const DELETE_BUTTON: TableColumnDefinition<Car, Car> = {
    name: 'delete',
    title: '',
    component: (car) => (
      <CarDeleteHandler carId={ car.id }>
        <TextButton qaMarker='delete-button'>
          Delete
        </TextButton>
      </CarDeleteHandler>
    ),
  }

  const COLUMNS: TableColumnDefinition<Car, any>[] = [
    MAKE_COLUMN,
    MODEL_COLUMN,
    YEAR_COLUMN,
    VALUE_COLUMN,
    EDIT_BUTTON,
    UNLINK_BUTTON,
    DELETE_BUTTON,
  ]

  const sorting: SortingProp = {
    direction: 'asc',
    columnName: 'make',
  }

  return (
    <section>
      <TypedTable columns={ COLUMNS } rows={ cars } sorting={ sorting } className={ styles.carsTable } />
    </section>
  )
}

const guards: Guards = [
  carsGuard,
]

export const CarSettingsModule = connectShadedLoadable<{}, {}, {}>(() => () => ({}), undefined, guards)(CarSettingsModuleComponent)

